import {
  FluidButtonType,
  FluidIconConfig,
  FluidOption,
  FluidPaginationState,
  FluidPaginatorTokens,
  FluidPaginatorTranslationOptions,
  FluidSequenceControl,
  FluidTheme,
} from '@lmig/fluid-core';
import { FunctionalComponent, h } from '@stencil/core';

import { FluidFunctionalComponentProps } from '../../../model/functional/fluid-functional-component-props.interface';
import { FluidI18nStore } from '../../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { themeVar } from '../../../utils/themes/theme.variables';
import { getRangeIndicatorWording } from '../utils/fluid-paginator.utils';

const { ITEMS_PER_PAGE } = FluidPaginatorTokens;

interface PaginatorProps extends FluidFunctionalComponentProps {
  paginationState?: FluidPaginationState;
  totalItems?: number;
  itemsPerPageOptions?: FluidOption[];
  showFirstButton?: boolean;
  showLastButton?: boolean;
  controlType?: FluidSequenceControl;
  pageValue?: any;
  pageNumber?: number;
  theme?: FluidTheme;
  overrideGlobalTheme?: boolean;
  rangeIndicatorTemplate?: string;
  itemsPerPageTemplate?: string;
  fluidTranslate?: FluidPaginatorTranslationOptions;
  i18nStore?: FluidI18nStore;
  identifier: string;
}

interface PaginatorSelectProps extends PaginatorProps {
  setSelectRef: (ref: HTMLFluidSelectElement) => void;
  i18nStore?: FluidI18nStore;
  fluidTranslate?: FluidPaginatorTranslationOptions;
}

// ================================================================ //
// -- Public Functional Components

/**
 * Paginator navigation container
 */
export const PaginatorNav: FunctionalComponent<PaginatorProps> = (
  props: PaginatorProps
) => {
  const {
    paginationState,
    showFirstButton,
    showLastButton,
    i18nStore,
    fluidTranslate,
    identifier,
  } = props;
  const pageSequence = calculatePageSequence(paginationState);

  return (
    <nav
      class='page-navigation'
      data-test-id={`${identifier}-page-nav`}
      role='navigation'
      aria-label={
        i18nStore?.translate(fluidTranslate?.ariaLabel.pageNavigation) ||
        'Pagination Navigation'
      }>
      <ul class='page-nav-list' data-test-id={`${identifier}-page-nav-list`}>
        {showFirstButton && (
          <PaginatorNavControl
            controlType={FluidSequenceControl.FIRST}
            {...props}
          />
        )}
        <PaginatorNavControl
          controlType={FluidSequenceControl.PREVIOUS}
          {...props}
        />
        <li class='page-nav-item'>
          <ul class='nav-button-list'>
            {pageSequence.map((pageVal: any) => (
              <PaginatorNavItem pageValue={pageVal} {...props} />
            ))}
          </ul>
        </li>
        <PaginatorNavControl
          controlType={FluidSequenceControl.NEXT}
          {...props}
        />
        {showLastButton && (
          <PaginatorNavControl
            controlType={FluidSequenceControl.LAST}
            {...props}
          />
        )}
      </ul>
    </nav>
  );
};

/**
 * Paginator page range indicator
 */
export const PaginatorRangeIndicator: FunctionalComponent<PaginatorProps> = (
  props: PaginatorProps
) => {
  const {
    paginationState,
    totalItems,
    rangeIndicatorTemplate,
    fluidTranslate,
    i18nStore,
    identifier,
  } = props;
  return (
    <div
      class='page-range-indicator'
      data-test-id={`${identifier}-page-range-indicator`}>
      {totalItems ? (
        <span class='page-range' data-test-id={`${identifier}-page-range`}>
          {getRangeIndicatorWording(
            paginationState,
            totalItems,
            i18nStore?.translate(fluidTranslate?.rangeIndicatorTemplate) ||
              rangeIndicatorTemplate
          )}
        </span>
      ) : (
        <span class='page-range' data-test-id={`${identifier}-page-range`}>
          {i18nStore?.translate(fluidTranslate?.noData) || 'No Data'}
        </span>
      )}
    </div>
  );
};

/**
 * Paginator page size dropdown menu
 */
export const PaginatorPageSizeSelect: FunctionalComponent<
  PaginatorSelectProps
> = (props: PaginatorSelectProps) => {
  const {
    itemsPerPageOptions,
    handlePageSizeChange,
    theme,
    overrideGlobalTheme,
    itemsPerPageTemplate,
    setSelectRef,
    i18nStore,
    fluidTranslate,
    identifier,
    selectedPageSize,
  } = props;
  const translatedTemplate =
    i18nStore?.translate(fluidTranslate?.itemsPerPageTemplate) ||
    itemsPerPageTemplate;
  const templateParts = translatedTemplate.split(ITEMS_PER_PAGE);

  return (
    <div
      class='page-size-select'
      data-test-id={`${identifier}-page-size-select`}>
      {templateParts.length === 2 && <span>{templateParts[0]}</span>}
      <fluid-select
        ref={(ref) => setSelectRef(ref)}
        data-test-id={`${identifier}-fluid-select`}
        options={itemsPerPageOptions}
        value={selectedPageSize || itemsPerPageOptions.at(0)?.value}
        placeholder='&nbsp;'
        onValueChange={(event) => handlePageSizeChange(event)}
        theme={theme}
        overrideGlobalTheme={overrideGlobalTheme}
        inlineField={true}
      />
      <span>
        {templateParts.length === 2 ? templateParts[1] : itemsPerPageTemplate}
      </span>
    </div>
  );
};

// ================================================================ //
// -- Private Functional Components

/**
 * Paginator navigation buttons (i.e. first, previous, next, last)
 */
const PaginatorNavControl: FunctionalComponent<PaginatorProps> = (
  props: PaginatorProps
) => {
  const {
    controlType,
    paginationState,
    goToPage,
    previousPage,
    nextPage,
    theme,
    overrideGlobalTheme,
    identifier,
  } = props;

  const navigateTo = (type: FluidSequenceControl) => {
    switch (type) {
      case FluidSequenceControl.FIRST:
        goToPage(0);
        break;
      case FluidSequenceControl.PREVIOUS:
        previousPage();
        break;
      case FluidSequenceControl.NEXT:
        nextPage();
        break;
      case FluidSequenceControl.LAST:
        goToPage(paginationState.totalPages - 1);
        break;
    }
  };

  return (
    <li
      class='page-nav-control'
      data-test-id={`${identifier}-page-nav-control-${controlType}`}>
      <fluid-button
        data-test-id={`${identifier}-page-nav-btn-${controlType}`}
        type={FluidButtonType.SECONDARY}
        onClick={() => navigateTo(controlType)}
        theme={theme}
        overrideGlobalTheme={overrideGlobalTheme}
        removeMargin={true}
        small={true}
        iconBefore={{
          ...getControlIcon(controlType, theme),
          theme: theme,
          overrideGlobalTheme: overrideGlobalTheme,
        }}
      />
    </li>
  );
};

/**
 * Paginator number button / ellipsis container
 */
const PaginatorNavItem: FunctionalComponent<PaginatorProps> = (
  props: PaginatorProps
) => {
  const { pageValue, identifier } = props;

  return (
    <li
      class='page-nav-item'
      data-test-id={`${identifier}-page-nav-item-${pageValue}`}>
      {pageValue === '...' ? (
        <span
          class='page-nav-ellipsis'
          data-test-id={`${identifier}-page-nav-ellipsis`}>
          ...
        </span>
      ) : (
        <PaginatorNavButton pageNumber={pageValue} {...props} />
      )}
    </li>
  );
};

/**
 * Paginator page number button
 */
const PaginatorNavButton: FunctionalComponent<PaginatorProps> = (
  props: PaginatorProps
) => {
  const {
    pageNumber,
    paginationState,
    goToPage,
    i18nStore,
    fluidTranslate,
    identifier,
  } = props;

  let ariaLabel: string;
  if (paginationState.currentPage === pageNumber) {
    ariaLabel = `${
      i18nStore?.translate(fluidTranslate?.ariaLabel?.currentPage) ||
      'Current Page, Page'
    } ${pageNumber + 1}`;
  } else {
    ariaLabel = `${
      i18nStore?.translate(fluidTranslate?.ariaLabel?.pageNumber) ||
      'Go To Page'
    } ${pageNumber + 1}`;
  }

  return paginationState.currentPage === pageNumber ? (
    <span
      data-test-id={`${identifier}-page-nav-btn-${pageNumber}`}
      aria-label={ariaLabel}
      aria-current='true'
      class={{
        'page-nav-button': true,
        'page-selected': paginationState.currentPage === pageNumber,
      }}>
      {pageNumber + 1}
    </span>
  ) : (
    <button
      onClick={() => goToPage(pageNumber)}
      class='page-nav-button'
      data-test-id={`${identifier}-page-nav-btn-${pageNumber}`}
      aria-label={ariaLabel}
      key={`page-${pageNumber + 1}`}>
      {pageNumber + 1}
    </button>
  );
};

// ================================================================ //
// -- Functional Component Utilities (Private)

/**
 * Determines which page numbers should be displayed & which page
 * numbers should be truncated via ellipsis based on current page.
 */
const calculatePageSequence = (state: FluidPaginationState) => {
  const { totalPages, currentPage } = state;
  const lastIndex = totalPages - 1;

  if (totalPages <= 7) {
    return [...Array(totalPages).keys()];
  } else {
    if (currentPage <= 3) {
      return [0, 1, 2, 3, 4, '...', lastIndex];
    } else if (currentPage >= lastIndex - 3) {
      return [
        0,
        '...',
        lastIndex - 4,
        lastIndex - 3,
        lastIndex - 2,
        lastIndex - 1,
        lastIndex,
      ];
    } else {
      return [
        0,
        '...',
        currentPage - 1,
        currentPage,
        currentPage + 1,
        '...',
        lastIndex,
      ];
    }
  }
};

/**
 * Returns theme-specific icon for first, previous, next, & last buttons.
 */
const getControlIcon = (type: FluidSequenceControl, iconTheme: FluidTheme) => {
  return themeVar<FluidIconConfig>(iconTheme, `icons.paginator.${type}`);
};
